<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'next/dialog/Dialog.vue';
import CustomAppForm from './CustomAppForm.vue';

const props = defineProps({
  integration: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const store = useStore();

const isOpen = ref(false);
const customAppRef = ref({});
const dialogRef = ref(null);
const formRef = ref(null);

const uiFlags = useMapGetter('wgptIntegrations/getUIFlags');

const handleSubmit = async () => {
  if (!formRef.value) return;
  const { state, v$ } = formRef.value;

  const isValid = await v$.$validate();
  if (!isValid) return;

  try {
    const hookPayload = {
      hookId: customAppRef.value.id,
      appId: props.integration.id,
      settings: {
        ...customAppRef.value.setings,
        ...state,
      },
    };

    await store.dispatch('wgptIntegrations/updateHook', hookPayload);

    const successMessage = t('WGPT_CUSTOM_APPS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(successMessage);
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error.message || t('WGPT_CUSTOM_APPS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleOpen = () => {
  isOpen.value = true;
};

const handleClose = () => {
  isOpen.value = false;
};

const close = () => {
  dialogRef.value.close();
  handleClose();
};

const open = customApp => {
  customAppRef.value = customApp;
  handleOpen();
  nextTick(() => {
    dialogRef.value.open();
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('WGPT_CUSTOM_APPS.EDIT.TITLE')"
    :description="t('WGPT_CUSTOM_APPS.EDIT.DESCRIPTION')"
    :is-loading="uiFlags.isUpdatingHook"
    :disable-confirm-button="uiFlags.isUpdatingHook"
    :cancel-button-label="t('WGPT_CUSTOM_APPS.EDIT.CANCEL')"
    :confirm-button-label="t('WGPT_CUSTOM_APPS.EDIT.UPDATE')"
    overflow-y-auto
    @confirm="handleSubmit"
    @close="handleClose"
  >
    <CustomAppForm
      v-if="isOpen"
      ref="formRef"
      :value="customAppRef"
      @submit="handleSubmit"
    />
  </Dialog>
</template>
